import { useToast } from 'native-base'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import type { ReactNode } from 'react'

import i18n from '../i18n'
import { ToastAlert, type ToastAlertProps } from '../components/Instructions/Common/ToastAlert'
import InstructionsModal from '../components/Instructions/InstructionsModal'
import { MapModal } from '../components/Map/MapModal'
import { isDelivered, onOrAfterScheduledServiceDate } from '@consumer/shared'
import { AppContext } from './AppContext'
import { AuthContext } from './AuthContext'
import {
  IAdjustGeopinToast,
  IEditAddressProps,
  ILocationUpdate,
  IToggleMapModalToastProps,
  OverlayState,
  ToggleModalFuncProps,
} from './OverlayContext'

interface ModalCustomProps {
  modalHeaderText?: string
  modalBodyText?: string
  cancelText?: string
}

const defaultPhoneModalProps: ModalCustomProps = {
  modalHeaderText: 'Login to edit details',
  modalBodyText: 'We need to confirm who you are in order to make any changes to your delivery',
  cancelText: i18n.t('common:CANCEL'),
}

const defaultVerificationModalProps: ModalCustomProps = {
  modalHeaderText: 'Login to edit details',
  modalBodyText: 'We need to confirm who you are in order to make any changes to your delivery',
  cancelText: i18n.t('common:CANCEL'),
}

const viewDetailsVerificationModalProps: ModalCustomProps = {
  modalHeaderText: 'Login to view details',
  modalBodyText: 'We need to confirm who you are in order to display sensitive information about this delivery',
  cancelText: i18n.t('common:CANCEL'),
}

const termsAndConditionsDefaultAction = 'making any changes to the delivery'

const defaultSubmitModalProps: ModalCustomProps = {
  modalHeaderText: 'Loading...',
}

const modalPropsInstructionsFlow: ModalCustomProps = {
  modalHeaderText: 'Confirm Instructions',
  modalBodyText: 'We need to verify your phone number to add delivery instructions.',
  cancelText: 'Add Instructions Later',
}

const modalPropsSubmitInstructionsFlow: ModalCustomProps = {
  modalHeaderText: 'Submitting Instructions...',
}

const termsAndConditionsInstructionsAction = 'adding instructions'

const modalPropsEditAddressFlow: ModalCustomProps = {
  modalHeaderText: 'Edit Address',
  modalBodyText: 'We need to confirm who you are in order to make any changes to your address.',
  cancelText: i18n.t('common:CANCEL'),
}

const termsAndConditionsEditAddressAction = 'updating the delivery address'

const phoneModalPropsViewPackageLocation: ModalCustomProps = {
  modalHeaderText: 'Login to view package',
  modalBodyText: "We need to confirm who you are in order to display your package's location.",
  cancelText: i18n.t('common:CANCEL'),
}

const termsAndConditionsViewPackageLocationAction = 'viewing package details'

/** Provides overlay and modal state (terms, photo lightbox, access code) to the component tree. */
function OverlayProvider({ children }: { children: ReactNode }) {
  const { order, proofOfServicePhotos, setOrder } = useContext(AppContext)
  const { firebaseUser, user } = useContext(AuthContext)
  const { show: showToast, close: closeToast } = useToast()
  const [modalOpen, setModalOpen] = useState<boolean>(false)
  const [showMapModal, setShowMapModal] = useState<boolean>(false)
  const [step, setStep] = useState<number>(0)
  const [phoneModalProps, setPhoneModalProps] = useState<ModalCustomProps>(defaultPhoneModalProps)
  const [verificationModalProps, setVerificationModalProps] = useState<ModalCustomProps>(defaultVerificationModalProps)
  const [termsConditionsAction, setTermsConditionsAction] = useState<string>(termsAndConditionsDefaultAction)
  const [submitModalProps, setSubmitModalProps] = useState<ModalCustomProps>(defaultSubmitModalProps)
  const [authFlow, setAuthFlow] = useState<ToggleModalFuncProps['auth_flow']>('general')
  const [clearAddressFields, setClearAddressFields] = useState<boolean>(false)
  const [geocodeUpdate, setGeocodeUpdate] = useState<ILocationUpdate>({ latitude: 0, longitude: 0 })
  const [useLocationUpdate, setUseLocationUpdate] = useState<boolean>(false)
  const [adjustGeopinToast, setAdjustGeopinToast] = useState<IAdjustGeopinToast>({
    headerText: 'Adjust Delivery Location',
    bodyText: 'Move pin on the map to update',
  })
  const [useUpdateNormalizedAddress, setUseUpdateNormalizedAddress] = useState<boolean>(false)
  const [postModalToast, setPostModalToast] = useState<{
    title: string
    description: string
    status: ToastAlertProps['status']
  } | null>(null)
  const hasShownToastRef = useRef<boolean>(false)

  const handleSetModalContent = useCallback(
    (auth_flow: ToggleModalFuncProps['auth_flow']) => {
      const orderIsDelivered: boolean = isDelivered(order!)
      const onOrAfterPkgSchedServiceDate: boolean = onOrAfterScheduledServiceDate(order!)

      switch (auth_flow) {
        case 'instructions':
          setTermsConditionsAction(termsAndConditionsInstructionsAction)
          setPhoneModalProps(modalPropsInstructionsFlow)
          setVerificationModalProps(modalPropsInstructionsFlow)
          setSubmitModalProps(modalPropsSubmitInstructionsFlow)
          setAuthFlow(auth_flow)
          break
        case 'edit_address':
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          orderIsDelivered
            ? setTermsConditionsAction(termsAndConditionsViewPackageLocationAction)
            : setTermsConditionsAction(
                onOrAfterPkgSchedServiceDate
                  ? termsAndConditionsViewPackageLocationAction
                  : termsAndConditionsEditAddressAction
              )
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          orderIsDelivered
            ? setPhoneModalProps(phoneModalPropsViewPackageLocation)
            : setPhoneModalProps(
                onOrAfterPkgSchedServiceDate ? viewDetailsVerificationModalProps : modalPropsEditAddressFlow
              )
          setVerificationModalProps(
            onOrAfterPkgSchedServiceDate ? viewDetailsVerificationModalProps : modalPropsEditAddressFlow
          )
          setSubmitModalProps(defaultSubmitModalProps)
          setAuthFlow(auth_flow)
          break
        case 'permanent_address_update':
          setTermsConditionsAction(termsAndConditionsEditAddressAction)
          setPhoneModalProps(modalPropsEditAddressFlow)
          setVerificationModalProps(modalPropsEditAddressFlow)
          setSubmitModalProps(defaultSubmitModalProps)
          setAuthFlow(auth_flow)
          break
        case 'general':
        default:
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          orderIsDelivered
            ? setTermsConditionsAction(termsAndConditionsViewPackageLocationAction)
            : setTermsConditionsAction(
                onOrAfterPkgSchedServiceDate
                  ? termsAndConditionsViewPackageLocationAction
                  : termsAndConditionsDefaultAction
              )
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          orderIsDelivered
            ? setPhoneModalProps(phoneModalPropsViewPackageLocation)
            : setPhoneModalProps(
                onOrAfterPkgSchedServiceDate ? viewDetailsVerificationModalProps : defaultPhoneModalProps
              )
          setVerificationModalProps(defaultVerificationModalProps)
          setSubmitModalProps(defaultSubmitModalProps)
          setAuthFlow(auth_flow)
          break
      }
    },
    [order]
  )

  /**
   * Handle toggling modal visibily and setting modal props (header text, body text, submit button text, etc)
   * @param showModal boolean - show or hide modal.
   * @param auth_flow string - used to dynamically update modal text and active step based on operation (instructions, edit address, general, etc).
   */
  const toggleModal = useCallback(
    ({ showModal, auth_flow }: { showModal: boolean; auth_flow: ToggleModalFuncProps['auth_flow'] }) => {
      const determineStep = () => {
        switch (auth_flow) {
          case 'instructions':
            return 0
          case 'edit_address':
            return firebaseUser ? 6 : 1
          case 'permanent_address_update':
            return firebaseUser ? 7 : 1
          default:
            return 1
        }
      }

      handleSetModalContent(auth_flow)
      setAuthFlow(auth_flow)
      setStep(determineStep())
      setModalOpen(showModal)
    },
    [handleSetModalContent, firebaseUser, setAuthFlow]
  )

  const handleSetModal = useCallback((value: boolean) => {
    setModalOpen(value)
  }, [])

  const handleSetStep = useCallback((stepNumber: number) => {
    setStep(stepNumber)
  }, [])

  const toggleMapModal = useCallback(
    ({ toastProps = {}, showToastAfterClose = false }: IToggleMapModalToastProps) => {
      setShowMapModal(!showMapModal)
      setUseUpdateNormalizedAddress(false)

      if (showToastAfterClose && toastProps?.title && toastProps?.description) {
        // Store toast info to show after modal closes
        setPostModalToast({
          title: toastProps?.title || '',
          description: toastProps?.description || '',
          status: toastProps?.status ?? 'success',
        })
      }
    },
    [showMapModal]
  )

  // Show toast after modal closes
  useEffect(() => {
    if (postModalToast && !showMapModal) {
      if (!hasShownToastRef.current) {
        hasShownToastRef.current = true
        showToast({
          placement: 'top',
          render: ({ id }) => (
            <ToastAlert
              id={id}
              title={postModalToast.title}
              description={postModalToast.description}
              status={postModalToast.status}
              variant={'subtle'}
              isClosable={true}
              closeToast={closeToast}
              toastAlertProps={{
                style: {
                  position: 'absolute',
                  top: 10,
                  zIndex: 5000,
                },
              }}
            />
          ),
          duration: 7000,
        })
        setPostModalToast(null)
      }
    } else {
      hasShownToastRef.current = false
    }
  }, [postModalToast, showMapModal, showToast, closeToast])

  const handleToggleClearAddressFields = useCallback(
    (clearFields?: boolean) => {
      setClearAddressFields(clearFields ?? !clearAddressFields)
    },
    [clearAddressFields]
  )

  const handleSetGeocodeUpdate = useCallback((location: ILocationUpdate) => {
    setGeocodeUpdate(location)
  }, [])

  const handleSetUseLocationUpdate = useCallback((useLocationUpdate: boolean) => {
    setUseLocationUpdate(useLocationUpdate)
  }, [])

  const handleEditAddress = useCallback(
    ({
      clearAddressFields = false,
      locationUpdate,
      useLocationUpdate = false,
      toggleMap = false,
    }: IEditAddressProps) => {
      if (toggleMap) {
        toggleMapModal({ showToastAfterClose: false })
      }

      if (clearAddressFields) {
        handleToggleClearAddressFields(true)
      }

      if (useLocationUpdate && locationUpdate?.latitude && locationUpdate?.longitude) {
        setUseLocationUpdate(useLocationUpdate)
        handleSetGeocodeUpdate(locationUpdate)
      }

      toggleModal({ showModal: true, auth_flow: 'edit_address' })
    },
    [toggleMapModal, toggleModal, handleToggleClearAddressFields, handleSetGeocodeUpdate, setUseLocationUpdate]
  )

  const handleSetAdjustGeopinToast = useCallback((toast: IAdjustGeopinToast) => {
    setAdjustGeopinToast(toast)
  }, [])

  const handleSetUseUpdateNormalizedAddress = useCallback((updateNormalizedAddress: boolean) => {
    setUseUpdateNormalizedAddress(updateNormalizedAddress)
  }, [])

  const state = useMemo(() => {
    return {
      modalOpen,
      toggleModal,
      step,
      handleSetStep,
      showMapModal,
      toggleMapModal,
      authFlow,
      clearAddressFields,
      handleClearAddressFields: handleToggleClearAddressFields,
      handleSetOrder: setOrder,
      geocodeUpdate,
      handleSetGeocodeUpdate,
      useLocationUpdate,
      setUseLocationUpdate: handleSetUseLocationUpdate,
      toggleEditAddress: handleEditAddress,
      adjustGeopinToast,
      handleSetAdjustGeopinToast,
      useUpdateNormalizedAddress,
      setUseUpdateNormalizedAddress: handleSetUseUpdateNormalizedAddress,
    }
  }, [
    modalOpen,
    toggleModal,
    step,
    handleSetStep,
    toggleMapModal,
    showMapModal,
    authFlow,
    clearAddressFields,
    handleToggleClearAddressFields,
    setOrder,
    geocodeUpdate,
    handleSetGeocodeUpdate,
    useLocationUpdate,
    handleSetUseLocationUpdate,
    handleEditAddress,
    adjustGeopinToast,
    handleSetAdjustGeopinToast,
    useUpdateNormalizedAddress,
    handleSetUseUpdateNormalizedAddress,
  ])

  return (
    <OverlayState.Provider value={state}>
      <MapModal
        order={order!}
        proofOfServicePhotos={proofOfServicePhotos}
        isOpen={showMapModal}
        onClose={() => setShowMapModal(false)}
        toggleMap={toggleMapModal}
        toggleModal={toggleModal}
        userAuthed={!!firebaseUser}
        acceptedTermsAndConditions={!!user?.agreedToTermsAndConditions}
        handleSetOrder={state.handleSetOrder}
        toggleEditAddress={state.toggleEditAddress}
        adjustGeopinToast={state.adjustGeopinToast}
        handleSetAdjustGeopinToast={handleSetAdjustGeopinToast}
        useUpdateNormalizedAddress={state.useUpdateNormalizedAddress}
        setUseUpdateNormalizedAddress={handleSetUseUpdateNormalizedAddress}
      />
      <InstructionsModal
        key={`instructions-modal-${geocodeUpdate.latitude}-${geocodeUpdate.longitude}`}
        order={order!}
        isOpen={modalOpen}
        setIsOpen={handleSetModal}
        setActiveStep={handleSetStep}
        activeStep={step}
        phoneModalProps={phoneModalProps}
        verificationModalProps={verificationModalProps}
        termsAndConditionsAction={termsConditionsAction}
        submitModalProps={submitModalProps}
        auth_flow={authFlow}
        // passing handlers and related values directly to avoid circular reference issues
        // InstructionsModal needs to remain a top level component due it being used for auth flow
        geocodeUpdate={geocodeUpdate}
        useLocationUpdate={useLocationUpdate}
        handleSetGeocodeUpdate={handleSetGeocodeUpdate}
        setUseLocationUpdate={handleSetUseLocationUpdate}
        clearAddressFields={clearAddressFields}
        handleClearAddressFields={handleToggleClearAddressFields}
        toggleEditAddress={handleEditAddress}
        handleSetAdjustGeopinToast={handleSetAdjustGeopinToast}
        toggleMapModal={toggleMapModal}
        useUpdateNormalizedAddress={state.useUpdateNormalizedAddress}
        setUseUpdateNormalizedAddress={handleSetUseUpdateNormalizedAddress}
      />
      {children}
    </OverlayState.Provider>
  )
}

export default OverlayProvider
